import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Dashboard from './components/Dashboard/Dashboard';
import DataBodyBuilder from './components/DataBodyBuilder/DataBodyBuilder';
import LoginPage from './components/Auth/LoginPage';
import MagicLinkCallback from './components/Auth/MagicLinkCallback';
import AcceptInvitePage from './components/Auth/AcceptInvitePage';
import UserManagement from './components/Admin/UserManagement';
import AccountSettings from './components/Account/AccountSettings';
import OrganizationSettings from './components/Admin/OrganizationSettings';
import IdentityGraph from './components/Admin/IdentityGraph';
import Automations from './components/Admin/Automations';
import Pixels from './components/Admin/Pixels';
import EnrichmentApiClients from './components/Admin/EnrichmentApiClients';
import ReportingPage from './components/Reporting/ReportingPage';
import ReportingBodyPage from './components/Reporting/ReportingBodyPage';
import ReportingSourcePage from './components/Reporting/ReportingSourcePage';
import ReportingClientPage from './components/Reporting/ReportingClientPage';
import ReportingLegPage from './components/Reporting/ReportingLegPage';
import SourcesPage from './components/Sources/SourcesPage';
import ClientsPage from './components/Clients/ClientsPage';
import { canSeeIdentityGraph } from './utils/access';

// The Identity Graph tab shows our GLOBAL owned-enrichment growth monitor.
// It's visible to super_admins from any org, PLUS org_admins who belong to
// the controlling org (Data Bodies HQ) — but no other org's admins.
//
// The rule now lives in utils/access.ts because IdentityGraph.tsx needs the
// SAME rule. It used to be defined only here, while the page itself gated on
// role === 'super_admin', so an HQ org_admin got the nav link and then a
// "Super admin access required." wall.

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'super_admin' && user?.role !== 'org_admin') {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function IdentityGraphRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!canSeeIdentityGraph(user)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function Sidebar() {
  const { user, organization, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'org_admin';

  const handleLogout = async () => {
    await logout();
    void navigate('/login');
  };

  // Exact match by default; a route also matches any path nested under it
  // (e.g. "/reports" stays selected for "/reports/bodies/:id") so sections
  // with sub-pages that don't get their own sidebar entry still highlight.
  // A link can list more than one root ("/new" + "/body" both render
  // DataBodyBuilder) when its section isn't a single URL prefix.
  const isActive = (...paths: string[]) =>
    paths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const navItemClass = (...paths: string[]) => `nav-item${isActive(...paths) ? ' active' : ''}`;

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="5" r="3"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="12" y1="16" x2="8" y2="22"/>
            <line x1="12" y1="16" x2="16" y2="22"/>
            <circle cx="12" cy="11" r="1"/>
            <circle cx="12" cy="14" r="1"/>
          </svg>
          Data Bodies
        </div>
        {organization && (
          <div className="sidebar-org">{organization.name}</div>
        )}
      </div>
      <div className="sidebar-nav">
        <Link to="/" className={navItemClass('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/>
            <rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/>
          </svg>
          Dashboard
        </Link>
        <div className="nav-section-label">Quick Actions</div>
        <Link to="/new" className={navItemClass('/new', '/body')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Build Data Body
        </Link>
        {/* Sources — the companies/people who send leads into data bodies.
            Sits under Quick Actions because it's a working directory, not an
            admin setting: you open it to look someone up or add a partner. */}
        <Link to="/sources" className={navItemClass('/sources')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 21h18"/>
            <path d="M5 21V7l7-4 7 4v14"/>
            <path d="M10 21v-5h4v5"/>
            <path d="M9 10h.01"/>
            <path d="M15 10h.01"/>
          </svg>
          Sources
        </Link>
        {/* Clients — the people/destinations that RECEIVE leads out of a leg.
            Sits directly under Sources so the pair reads as the two ends of
            the same pipe: who sends leads in, who they go out to. */}
        <Link to="/clients" className={navItemClass('/clients')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9.5" cy="7" r="4"/>
            <path d="M22 11h-6"/>
            <path d="M19 8l3 3-3 3"/>
          </svg>
          Clients
        </Link>
        <Link to="/reports" className={navItemClass('/reports')}>
          {/* Bar chart icon for Reporting. Single non-expanding entry —
              all sub-views (overview, body drilldown, node drilldown) live
              inside /reports per Scott's "no sidebar expansion" rule. */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/>
            <line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
            <line x1="3" y1="20" x2="21" y2="20"/>
          </svg>
          Reporting
        </Link>

        {isAdmin && (
          <>
            <div className="nav-section-label">Admin</div>
            <Link to="/admin/users" className={navItemClass('/admin/users')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Users & Invites
            </Link>
            <Link to="/admin/organization" className={navItemClass('/admin/organization')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Org Settings
            </Link>
            {canSeeIdentityGraph(user) && (
              <Link to="/admin/identity" className={navItemClass('/admin/identity')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="5" cy="6" r="2"/>
                  <circle cx="19" cy="6" r="2"/>
                  <circle cx="12" cy="18" r="2"/>
                  <path d="M7 6h10"/>
                  <path d="M6 8l5 8"/>
                  <path d="M18 8l-5 8"/>
                </svg>
                Identity Graph
              </Link>
            )}
            {user?.role === 'super_admin' && (
              <Link to="/admin/automations" className={navItemClass('/admin/automations')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7"/>
                  <rect x="14" y="3" width="7" height="7"/>
                  <rect x="3" y="14" width="7" height="7"/>
                  <rect x="14" y="14" width="7" height="7"/>
                </svg>
                Automations
              </Link>
            )}
            {user?.role === 'super_admin' && (
              <Link to="/admin/pixels" className={navItemClass('/admin/pixels')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <circle cx="12" cy="12" r="8"/>
                  <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                </svg>
                Pixels
              </Link>
            )}
            {user?.role === 'super_admin' && (
              <Link to="/admin/enrichment-api" className={navItemClass('/admin/enrichment-api')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="7.5" cy="15.5" r="4.5"/>
                  <path d="M10.7 12.3 21 2M16 7l3 3M18.5 4.5l2 2"/>
                </svg>
                Enrichment API
              </Link>
            )}
          </>
        )}
      </div>

      <div className="sidebar-footer">
        <Link to="/account" className={`sidebar-user${isActive('/account') ? ' active' : ''}`} title="My account">
          <div className="sidebar-user-avatar">
            {(user?.firstName?.[0] || user?.email[0] || '?').toUpperCase()}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">
              {[user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email}
            </div>
            <div className="sidebar-user-role">{user?.role.replace('_', ' ')}</div>
          </div>
        </Link>
        <button className="sidebar-logout" onClick={handleLogout} title="Sign out">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

/**
 * The "you are not you right now" bar. Renders on EVERY authenticated page
 * whenever the current access token carries an impersonation stamp, which the
 * backend re-applies on each token refresh — so it cannot silently disappear
 * partway through a session.
 *
 * This is the only thing standing between a super_admin and forgetting which
 * account they're typing into, so it is deliberately loud and deliberately
 * fixed to the top rather than scrolling away.
 */
function ImpersonationBanner() {
  const { impersonation, user, organization, stopImpersonation } = useAuth();
  const navigate = useNavigate();

  if (!impersonation) {return null;}

  const handleExit = async () => {
    await stopImpersonation();
    void navigate('/admin/users');
  };

  const who = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        // Starts where the sidebar ends (.sidebar is position:fixed, 260px
        // wide, top:0) so the bar can't cover the logo/nav.
        left: 260,
        right: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        flexWrap: 'wrap',
        padding: '9px 16px',
        background: '#b45309',
        color: '#fff',
        fontSize: 13,
        fontWeight: 600,
        boxShadow: '0 1px 6px rgba(0,0,0,0.25)',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
      <span>
        Viewing as <strong>{who}</strong>
        {user?.email && who !== user.email && <span style={{ fontWeight: 400 }}> ({user.email})</span>}
        {organization && <span style={{ fontWeight: 400 }}> — {organization.name}</span>}
        <span style={{ fontWeight: 400, opacity: 0.85 }}> · changes you make are saved as this user</span>
      </span>
      <button
        onClick={handleExit}
        style={{
          background: '#fff',
          color: '#b45309',
          border: 'none',
          borderRadius: 5,
          padding: '4px 12px',
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Exit to my account
      </button>
    </div>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  const { impersonation } = useAuth();
  return (
    <div className="app-layout">
      <ImpersonationBanner />
      <Sidebar />
      {/* Push content down so the fixed banner never covers the page header.
          .main-content's own padding is 32px, so 32 + banner height. */}
      <div className="main-content" style={impersonation ? { paddingTop: 74 } : undefined}>
        {children}
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/auth/magic" element={<MagicLinkCallback />} />
          <Route path="/auth/invite" element={<AcceptInvitePage />} />

          {/* Protected routes */}
          <Route path="/" element={
            <ProtectedRoute>
              <AppLayout><Dashboard /></AppLayout>
            </ProtectedRoute>
          } />
          {/* Any authenticated user, not just admins -- this is their own
              account, reached from the sidebar's user block. */}
          <Route path="/account" element={
            <ProtectedRoute>
              <AppLayout><AccountSettings /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/new" element={
            <ProtectedRoute>
              <AppLayout><DataBodyBuilder /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/body/:id" element={
            <ProtectedRoute>
              <AppLayout><DataBodyBuilder /></AppLayout>
            </ProtectedRoute>
          } />

          <Route path="/sources" element={
            <ProtectedRoute>
              <AppLayout><SourcesPage /></AppLayout>
            </ProtectedRoute>
          } />

          <Route path="/clients" element={
            <ProtectedRoute>
              <AppLayout><ClientsPage /></AppLayout>
            </ProtectedRoute>
          } />

          {/* Reporting — all sub-views live inside this prefix, but the
              sidebar entry only points at /reports (no expansion). */}
          <Route path="/reports" element={
            <ProtectedRoute>
              <AppLayout><ReportingPage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/bodies/:id" element={
            <ProtectedRoute>
              <AppLayout><ReportingBodyPage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/sources/:id" element={
            <ProtectedRoute>
              <AppLayout><ReportingSourcePage /></AppLayout>
            </ProtectedRoute>
          } />
          <Route path="/reports/clients/:id" element={
            <ProtectedRoute>
              <AppLayout><ReportingClientPage /></AppLayout>
            </ProtectedRoute>
          } />
          {/* One delivery destination. Simpler than the body drilldown by
              design — a leg-node belongs to exactly one body, so there is no
              Brain/Spine breakdown to show. */}
          <Route path="/reports/legs/:id" element={
            <ProtectedRoute>
              <AppLayout><ReportingLegPage /></AppLayout>
            </ProtectedRoute>
          } />

          {/* Admin routes */}
          <Route path="/admin/users" element={
            <AdminRoute>
              <AppLayout><UserManagement /></AppLayout>
            </AdminRoute>
          } />
          <Route path="/admin/organization" element={
            <AdminRoute>
              <AppLayout><OrganizationSettings /></AppLayout>
            </AdminRoute>
          } />
          <Route path="/admin/identity" element={
            <IdentityGraphRoute>
              <AppLayout><IdentityGraph /></AppLayout>
            </IdentityGraphRoute>
          } />
          {/* Automations board — super_admin only (enforced in the component + backend). */}
          <Route path="/admin/automations" element={
            <ProtectedRoute>
              <AppLayout><Automations /></AppLayout>
            </ProtectedRoute>
          } />
          {/* Identity Pixels — super_admin only (enforced in the component + backend). */}
          <Route path="/admin/pixels" element={
            <ProtectedRoute>
              <AppLayout><Pixels /></AppLayout>
            </ProtectedRoute>
          } />
          {/* BigSky Enrichment API clients + keys — super_admin only (enforced in the component + backend). */}
          <Route path="/admin/enrichment-api" element={
            <ProtectedRoute>
              <AppLayout><EnrichmentApiClients /></AppLayout>
            </ProtectedRoute>
          } />

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
